'use client';

import { useEffect, useRef, useState } from 'react';
import useUI from '@app/components/ui/state';
import { SwiperBlock, SwiperItem } from '@app/components/swiper/type';
import SwiperPDPGalleryVariant from '@app/components/swiper/SwiperPDPGallery.variant';
import ProductBuy from '@app/[lang]/products/product-buy/product-buy';
import { useParams } from 'next/navigation';
import { Language } from '@app/lib/store-context/Context';
import ProductTruefit from '../product-truefit/product-truefit';
import { trimGID } from '@app/lib/shopify/helpers/gid';
import ProductTruefitDrawer from '../product-truefit/product-truefit-drawer';
import { createGalleryItems } from '../utilities';
import cn from 'clsx';
import s from './product-hero.module.css';

const ProductHero = ({
    id,
    title,
    images,
    videos,
    sizes,
    price,
    compareAtPrice,
    currencyCode,
}) => {
    const setProductHeroRef = useUI((state) => state.setProductHeroRef);
    const ref = useRef(null);
    const { lang } = useParams();
    const [iframeSrc, setIframeSrc] = useState('');
    const { openUIKey, toggleUI } = useUI();
    const [triggerRefetch, setTriggerRefetch] = useState(false);

    const locale =
        lang === 'en-UK' ? 'en_GB' : (lang as string).replace('-', '_');

    let productID: string | undefined;
    if (id) {
        productID = trimGID(id);
    }

    useEffect(() => {
        setProductHeroRef(ref);
    }, [setProductHeroRef]);

    // Truefit
    useEffect(() => {
        const handleMessage = (event) => {
            if (event.origin.indexOf('truefitcorp.com') !== -1) {
                const msg = JSON.parse(event.data);
                if (msg.message === 'close') {
                    toggleUI(openUIKey);
                }
                if (msg.message === 'usertoken') {
                    localStorage.setItem('trueFitToken', msg.data.value);
                }
            }
        };

        window.addEventListener('message', handleMessage);

        return () => {
            window.removeEventListener('message', handleMessage);
        };
    }, []);

    const onCTAClick = (src, uiKey) => {
        setIframeSrc(src);
        toggleUI(uiKey);
    };

    const galleryItems: SwiperItem[] = createGalleryItems(images, videos);

    const galleryBlock: SwiperBlock = {
        variant: 'pdp_gallery',
        items: galleryItems,
        context: '',
    };

    return (
        <div className={cn(s.negativeMargin, s.overflowClip)} ref={ref}>
            <div className={cn(s.cover)}>
                <div className={cn(s.repel, s.overview)}>
                    <h1 className={cn(s.title, s.mixBlend, 'mmds-title-two')}>
                        {title}
                    </h1>
                    <div
                        className={cn(
                            s.cluster,
                            s.buyContainer,
                            'hidden-on-mobile',
                            'mmds-component-one-detail'
                        )}
                    >
                        <ProductBuy
                            sizes={sizes}
                            price={price}
                            compareAtPrice={compareAtPrice}
                            currencyCode={currencyCode}
                            inPDPHero
                        />
                    </div>
                    <div className={cn(s.truefit, 'hidden-on-mobile')}>
                        {productID && (
                            <ProductTruefit
                                productID={productID}
                                locale={locale}
                                onCTAClick={onCTAClick}
                                uiKey="truefit-desktop"
                                triggerRefetch={triggerRefetch}
                            />
                        )}
                        {iframeSrc && (
                            <ProductTruefitDrawer
                                uiKey="truefit-desktop"
                                ctaURL={iframeSrc}
                                setTriggerRefetch={setTriggerRefetch}
                            />
                        )}
                    </div>
                </div>
                <SwiperPDPGalleryVariant
                    block={galleryBlock}
                    context={{ lang: lang as Language }}
                />
            </div>
            {/* MOBILE ONLY */}
            <hr className="divider hidden-on-mobile" />
            <div className={cn('hidden-on-desktop', s.fitContent)}>
                <ProductBuy
                    sizes={sizes}
                    price={price}
                    compareAtPrice={compareAtPrice}
                    currencyCode={currencyCode}
                />
                {productID && (
                    <ProductTruefit
                        productID={productID}
                        locale={locale}
                        onCTAClick={onCTAClick}
                        uiKey="truefit-mobile"
                        triggerRefetch={triggerRefetch}
                    />
                )}
                {iframeSrc && (
                    <ProductTruefitDrawer
                        uiKey="truefit-mobile"
                        ctaURL={iframeSrc}
                        setTriggerRefetch={setTriggerRefetch}
                    />
                )}
            </div>
        </div>
    );
};

export default ProductHero;
